import {
  Component,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CwBanner, CwSnackbar, CwSplashScreen } from '@bbraun/cortex-angular';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, filter, from, merge, of, retry, skip, skipWhile, tap, throwError } from 'rxjs';

import { InactivityDetectionService } from '@/app/core/services/inactivity-detection/inactivity-detection.service';
import { ShellComponent } from '@/app/core/shell/shell.component';
import { DialogComponent } from '@/app/core/ui/dialog/dialog.component';
import { ScreenNotSupportedComponent } from '@/app/core/ui/screen-not-supported/screen-not-supported.component';
import { SidebarComponent } from '@/app/core/ui/sidebar/sidebar.component';
import { DialogHelperService } from '@/app/shared/ui-common/dialog/dialog-helper/dialog-helper.service';
import { SnackbarNotifierService } from '@/app/shared/ui-common/snackbar/snackbar.service';
import { SanitizeHTMLPipe } from '@/app/shared/util-common/sanitize-html/sanitize-html.pipe';
import { UserContextState } from '@/app/shared/util-user/user-context._state';
import { TranslocoRootModule } from '@/app/transloco-root.module';

export const SCREEN_MIN_SUPPORTED_WIDTH = 1024;

export const SCREEN_SMALL_SIZE = 1280;

@Component({
  selector: 'app-root',
  imports: [
    ShellComponent,
    TranslocoRootModule,
    ScreenNotSupportedComponent,
    CwSplashScreen,
    DialogComponent,
    CwBanner,
    SanitizeHTMLPipe,
    SidebarComponent,
  ],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  private readonly snackbarNotifierService = inject(SnackbarNotifierService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogHelper = inject(DialogHelperService);
  private readonly inactivityDetectionService = inject(InactivityDetectionService);
  private unsupportedBrowserTypeDialogTemplate = viewChild('unsupportedBrowserTypeDialogTemplate', {
    read: TemplateRef,
  });
  private unsupportedBrowserVersionDialogTemplate = viewChild(
    'unsupportedBrowserVersionDialogTemplate',
    {
      read: TemplateRef,
    },
  );
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly verifyBrowserCompatibility$ = from(
    typeof BrowserUtils !== 'undefined' &&
      typeof BrowserUtils.verifyBrowserCompatibility === 'function'
      ? BrowserUtils.verifyBrowserCompatibility()
      : throwError(() => new Error('[browser compatibility unknown] script not provided')),
  );

  userContextState = inject(UserContextState);

  isScreenNotSupported = false;
  isSmallScreen = false;
  isFirstCall = true;
  previousScreenWidth!: number;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private getTemplateEffect = effect(() => {
    const templateType = this.unsupportedBrowserTypeDialogTemplate();
    const templateVersion = this.unsupportedBrowserVersionDialogTemplate();

    if (templateType && templateVersion) {
      this.handleUnsupportedBrowser(templateType, templateVersion);
    }
  });

  ngOnInit(): void {
    this.previousScreenWidth = window.innerWidth;
    this.initializeSnackbars();

    this.userContextState.authenticateUser();
    this.userContextState.authorizeUser();
    this.userContextState.getUserSiteContext();
    this.inactivityDetectionService.startTracking();

    merge(
      this.handleInitialWindowResize(),
      this.userContextState.attachSiteIdOnNavigationEndEffect(),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.handleSmallScreenErrors();
    this.previousScreenWidth = window.innerWidth;
  }

  private handleSmallScreenErrors(shouldCheckPreviousScreen = true) {
    this.isScreenNotSupported = window.innerWidth < SCREEN_MIN_SUPPORTED_WIDTH;
    this.isSmallScreen = window.innerWidth < SCREEN_SMALL_SIZE;

    if (this.isScreenNotSupported) {
      this.clearDialogs();
      this.clearSnackbars();
      return;
    }
    this.initializeSnackbars();

    const shouldShowSnackbar = shouldCheckPreviousScreen
      ? this.previousScreenWidth >= SCREEN_SMALL_SIZE ||
        this.previousScreenWidth < SCREEN_MIN_SUPPORTED_WIDTH
      : true;

    if (this.isSmallScreen && shouldShowSnackbar) {
      this.snackbarNotifierService.callSnackbar({
        type: 'info',
        message: this.translocoService.translate('errors.smallScreen.snackbarText'),
      });
      return;
    }

    if (!this.isSmallScreen && this.previousScreenWidth < SCREEN_SMALL_SIZE) {
      this.clearSnackbars();
      return;
    }
    this.initializeSnackbars();
  }

  private handleInitialWindowResize() {
    return this.translocoService.events$.pipe(
      skip(1),
      skipWhile(() => this.isScreenNotSupported),
      tap(() => {
        if (this.isFirstCall) {
          this.handleSmallScreenErrors(false);
        } else {
          this.isFirstCall = false;
        }
      }),
      catchError(() => of(null)),
    );
  }

  private clearSnackbars() {
    const cwSnackbarElement = document.querySelector('cw-snackbar');
    cwSnackbarElement?.remove();
  }

  private clearDialogs() {
    this.dialogHelper.close();
  }

  private initializeSnackbars() {
    const cwSnackbarElement = document.querySelector('cw-snackbar');
    if (!cwSnackbarElement) {
      this.viewContainerRef.createComponent(CwSnackbar);
    }
  }

  private handleUnsupportedBrowser(
    templateType: TemplateRef<any>,
    templateVersion: TemplateRef<any>,
  ) {
    this.verifyBrowserCompatibility$
      .pipe(
        retry({ count: 3, delay: 3000 }),
        catchError((err) => {
          console.log(err.message);
          return of(null);
        }),
        filter((result) => !!result),
        tap((result) => {
          if (result.isUnsupportedType) {
            this.dialogHelper.open({
              title: 'errors.unsupportedBrowserType.title',
              cancelButton: 'errors.unsupportedBrowserType.button',
              template: templateType,
            });
          }
          if (result.isUnsupportedVersion) {
            this.dialogHelper.open({
              title: 'errors.unsupportedBrowserVersion.title',
              cancelButton: 'errors.unsupportedBrowserVersion.button',
              template: templateVersion,
            });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
